import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useEffect, useState, useContext } from 'react';
import { Row, Col, Image } from 'react-bootstrap';
import { Spinner, Text, Button } from '@radix-ui/themes';
import { ArrowClockwise } from '@phosphor-icons/react';
import AudioRecorder from './components/AudioRecorder';
import Vapi from "@vapi-ai/web";
import { ThemeContext } from './Theme';
import { v4 as uuidv4 } from 'uuid';
import toast, { Toaster } from 'react-hot-toast';
import { useMediaQuery } from './shared-functions.js';
import { Helmet } from 'react-helmet';
import useVisitorInfo from './utilities/useVisitorInfo';

export default function Home() {

    const { id } = useParams();
    const { theme } = useContext(ThemeContext);
    const navigate = useNavigate();
    let isPageWide = useMediaQuery('(min-width: 960px)');
    const visitorInfo = useVisitorInfo();

    // Capture search params
    const [searchParams] = useSearchParams();

    const [isEmbedded, setIsEmbedded] = useState(false);
    const [inviteCode, setInviteCode] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [study, setStudy] = useState(null);
    const [connected, setConnected] = useState(false);
    const [connecting, setConnecting] = useState(false);
    const [volumeLevel, setVolumeLevel] = useState(0);
    const [sessionId, setSessionId] = useState(null);
    const [assistantIsSpeaking, setAssistantIsSpeaking] = useState(false);
    const [assistantCaptions, setAssistantCaptions] = useState(' '); //How is your experience with the new AI content creator assistant?, How do you feel about our new earth-tone color palette in the latest collection?
    const [conversationEnded, setConversationEnded] = useState(false);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');

    const [vapi] = useState(() => new Vapi(process.env.REACT_APP_VAPI_PUBLIC_KEY));
    const [vapiSecret] = useState(() => process.env.REACT_APP_VAPI_SECRET_KEY);

    useEffect(() => {
        if (id && id.length === 36) { // Check if id is a valid UUID
            initialize();
        } else {
            navigate('/notfound');
            setIsLoading(false);
        }
    }, [id]);

    // Generate random volume level
    // useEffect(() => {
    //     const volumeInterval = setInterval(() => {
    //         setVolumeLevel(Math.random() > 0.5);
    //     }, 100);

    //     return () => clearInterval(volumeInterval);
    // }, []);

    // Generate random captions
    // useEffect(() => {
    //     const captions = [
    //         "When you think of Acme, what comes to mind first?",
    //         "How do you feel Acme compares to other athletic wear brands like Zike or Over Armor?",
    //         "What do you think about the quality and durability of Acme products?",
    //         "How would you describe Acme's design aesthetic and style?",
    //         "Tell me about your experience with Acme footwear - what stands out to you?",
    //         "What role does Acme play in your athletic activities or everyday wear?",
    //         "How do you feel about Acme's pricing compared to similar brands?",
    //         "What do you think about Acme's innovation in sportswear technology?",
    //         "How well do you think Acme understands athletes' needs?",
    //         "What would make you more likely to choose Acme over other brands?"
    //     ]
    //     const captionsInterval = setInterval(() => {
    //         setAssistantCaptions(captions[Math.floor(Math.random() * captions.length)]);
    //     }, 10000);
    //     return () => clearInterval(captionsInterval);
    // }, []);

    const initialize = async () => {
        try {
            
            // Set embedded mode
            if (searchParams.get('embed') === 'true') {
                setIsEmbedded(true);
            }
            
            // Capture invite code from URL
            const inviteCode = searchParams.get('invite');
            if (inviteCode && inviteCode.length > 0) {
                setInviteCode(inviteCode);
            }

            // Find study by id
            let res = await fetch(`${process.env.REACT_APP_VOICEBRIDGE_API_GET_STUDY_URL}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${process.env.REACT_APP_VOICEBRIDGE_API_KEY}`
                },
                body: JSON.stringify({
                    pageCode: id
                })
            });
            if (res.ok) {
                let data = await res.json();
                if (data.study) {
                    setStudy(data.study);
                    setIsLoading(false);
                } else {
                    navigate('/notfound');
                    setIsLoading(false);
                }
            } else {
                navigate('/notfound');
                setIsLoading(false);
            }
        } catch (error) {
            // console.error(error);
            navigate('/notfound');
            setIsLoading(false);
        }
    }

    // hook into Vapi events
    useEffect(() => {

        vapi.on("call-start", () => {
            setConnecting(false);
            setConnected(true);
        });

        // Update and display conversation transcript
        vapi.on("message", (message) => {
            if (message.type === 'transcript' && message.role === 'assistant' && message.transcriptType === 'final') {
                setAssistantCaptions(message.transcript);
            }
        });

        vapi.on("call-end", async () => {
            setConnecting(false);
            setConnected(false);
            // Update session status to 'completed'
            if (sessionId) {
                // Call API to update session status to 'completed'
                await updateSessionStatus(sessionId, study.workspaceId, 'completed');
                setSessionId(null);
            }
            setConversationEnded(true);
            setAssistantCaptions(' ');
        });

        vapi.on("speech-start", () => {
            setAssistantIsSpeaking(true);
        });

        vapi.on("speech-end", () => {
            setAssistantIsSpeaking(false);
        });

        vapi.on("volume-level", (level) => {
            setVolumeLevel(level);
        });

        vapi.on("error", async (error) => {
            // console.error(error);
            setConnecting(false);
            if (sessionId) {
                // Call API to update session status to 'failed'
                await updateSessionStatus(sessionId, study.workspaceId, 'failed');
                setSessionId(null);
            }
            setConversationEnded(true);
        });

    }, []);

    const generateAssistantOptions = async (sessionId) => {

        let content = ''; // Identity, Style Guardrails, Response Guidelines, Knowledge, Call Task, End call message

        // Identity
        content += `## Identity\n`;
        content += `You are an expert survey moderator who is tasked with asking participants questions and collecting answers from them. You are a pleasant and friendly conversationalist. You understand responses from the participant like a human would, respond to them as best as you can, and try to get answers to all the questions below.`;
        content += `\n\n`;

        // Style Guardrails
        content += `## Style Guardrails\n`;
        content += `
        Be Concise: Respond succinctly, addressing one topic at most. 
        
        Embrace Variety: Use diverse language and rephrasing to enhance clarity without repeating content.
        
        Be Conversational: Use everyday language, making the chat feel like talking to a friend. 
        
        Be Proactive: Lead the conversation, often wrapping up with a question or next-step suggestion. Avoid multiple questions in a single response. 
        
        Get clarity: If the user only partially answers a question, or if the answer is unclear, keep asking to get clarity. 
        
        Use a colloquial way of referring to the date (like Friday, January 14th, or Tuesday, January 12th, 2025 at 8am).`;

        content += `\n\n`;

        // Response Guidelines
        content += '## Response Guidelines\n';
        content += `
        - Adapt and Guess: Try to understand transcripts that may contain transcription errors. Avoid mentioning "transcription error" in the response. 
        
        - Stay in Character: Keep conversations within your role's scope, guiding them back creatively without repeating. 
        
        - Ensure Fluid Dialogue: Respond in a role-appropriate, direct manner to maintain a smooth conversation flow. Do not mention function call names or say "Ending the call" before ending the call.
        
        - Maintain neutrality to avoid biasing participant responses. 
        
        - Allow silence after open-ended questions for respondent to think.
        
        - Acknowledge all feedback non-judgmentally, whether positive or negative.

        - Do not interrupt the participant or respond too quickly.

        - Use minimal acknowledging responses to avoid influencing subsequent answers.

        - Respect "don't know" or "prefer not to answer" responses without pressing for more information.

        - For particpants giving very brief answers, use neutral probing techniques: e.g. "Could you tell me a bit more about that?", or ask for specifics: e.g. "Could you share a specific example of when that happened?".

        - For critical or negative feedback, receive it openly, avoid defensiveness, explore constructively and do not justify or explain.
        `;
        
        content += `\n\n`;

        // For particpants giving very brief answers, ask follow up questions to encourage more information.
        content += `
        - For particpants giving very brief answers, ask follow up questions to encourage more information.
        `;

        // Knowledge
        content += '## Knowledge\n';
        content += `${study.prompt?.knowledge || ''}`;
        content += `\n\n`;

        // Conversation Task
        content += '## Conversation Task\n';
        content += `
        You will ask the study participant the questions below, do not skip any questions, and only ask up to one question in response. 
        
        After every answer from the participant, wait a natural pause (as a human would) before giving a brief, sincere acknowledgement. Use a variety of short phrases such as "I see", "That makes sense", "Thanks for sharing", or "Interesting!". Match your tone to the participant's response, and avoid repeating the same phrase consecutively. Do not interrupt the participant or respond too quickly. Ask and give space, allowing for silence for thinking, and then move on to the next question.
        
        If at any time the participant shows anger, hang up immediately using the endCall function. 

        If at any time the participant wants to stop, hang up using the endCall function.
        
        If at any time the study participant wants to talk to a human, tell them you will let the team know and hang up using the endCall function.
        
        If the study participant has questions, answer them based on the knowledge provided to you and then return to the conversation task. If you do not know the answer, just say you do not know the answer, you will make a note of it, and then return to the conversation task. Do not make up answers. 
        
        If the particpant does not answer the question, ask a follow up question. If the particpant still does not answer the question, move on to the next question.
        
        Do not perform a task that is not in the sequence of tasks below:`;
        content += `\n\n`;

        // Questions
        content += '## Questions\n';
        content += study.questions.map((question, index) =>
            `${index + 1}. ${question.question}\n Make sure their response is a ${question.type.toLowerCase()}. ${question.followup ? `Ask one or two thoughtful follow-up questions based on the participant's response. Your goal is to encourage the participant to elaborate, clarify, or reflect, so that they provide more depth, detail, or examples. Use a mix of follow-up question types such as clarification, elaboration, why/reasoning, comparison, or feeling-oriented questions. Avoid yes/no questions. Each follow-up should feel natural and build directly on what the participant just said. Then move to the next question.` : 'Do not ask any follow-up questions and move to the next question.'}` +
            `\n\n`
        ).join('\n');
        content += `\n\n`;

        // End Call
        content += `After you have asked all the questions, hangup immediately using the endCall function. Do not mention that the call is ending or that you are ending the call or that you are calling a function or anything like that.`;
        content += `\n\n`;

        // console.log(content);

        let vapiAssistantOptions = {
            name: study.name || "Untitled Study" + " (test)",
            voice: {
                model: study.voice?.model || "eleven_turbo_v2_5",
                voiceId: study.voice?.voiceId || "dN8hviqdNrAsEcL57yFj",
                provider: study.voice?.provider || "11labs",
                stability: study.voice?.stability || 0.5,
                similarityBoost: study.voice?.similarityBoost || 0.75,
                useSpeakerBoost: study.voice?.useSpeakerBoost || true,
                fillerInjectionEnabled: study.voice?.fillerInjectionEnabled || false,
                optimizeStreamingLatency: study.voice?.optimizeStreamingLatency || 2
            },
            model: {
                model: study.model?.model || "gpt-4o",
                provider: study.model?.provider || "openai",
                temperature: study.model?.temperature || 0.7,
                emotionRecognitionEnabled: study.model?.emotionRecognitionEnabled || true,
                toolIds: study.model?.toolIds || [],
                messages: [
                    {
                        role: "system",
                        content: `${content}`
                    }
                ],
            },
            recordingEnabled: study.recordingEnabled || true,
            maxDurationSeconds: study.maxDurationSeconds || 300,
            silenceTimeoutSeconds: study.silenceTimeoutSeconds || 20,
            firstMessage: study.prompt?.firstMessage || "Hello, I am an AI moderator. Can you answer a few questions for me?",
            voicemailMessage: study.prompt?.voicemailMessage || "Hey, can you please call me back?",
            endCallFunctionEnabled: study.endCallFunctionEnabled || true,
            endCallMessage: study.prompt?.closingMessage || "Thank you for talking to me. Have a great day!",
            transcriber: {
                provider: "deepgram", // study.transcriber?.provider || "deepgram"
                model: "nova-3", // study.transcriber?.model || "nova-3",
                language: "en" // study.transcriber?.language || "en",
            },
            clientMessages: [
                "function-call",
                "transcript"
            ],
            serverMessages: [
                "end-of-call-report"
            ],
            server: {
                url: study.serverUrl || "https://us-central1-voicebridge-app.cloudfunctions.net/vapiWebhook",
                secret: vapiSecret,
                headers: {
                    'session': sessionId || null,
                    'workspace': study.workspaceId || null
                }
            },
            // endCallPhrases: study.endCallPhrases || [
            //     "goodbye",
            //     "thank you",
            //     "thank you for your time",
            // ],
            backgroundSound: study.backgroundSound || "office",
            backchannelingEnabled: study.backchannelingEnabled || true,
            analysisPlan: {
                summaryPrompt: "You are an expert note taker. You will be given the transcript of conversation between a survey participant and an AI survey bot. Summarize the conversation in 50 words or less with emphasis on the participant's response to the questions. Do not start your summary with 'Here is a summary of the call...' or anything similar.",
                //  with the goal of answering the following questions: " + study.questions.map((question) => `${question.question}`).join(', ') + "
                structuredDataPrompt: "You will be given the transcript of a conversation between a survey participant and an AI survey bot. Extract the overall sentiment and title of the call based on the participant's response to the questions.",
                structuredDataSchema: {
                    type: "object",
                    properties: {
                        sentiment: {
                            description: "The overall sentiment of the participant during the call",
                            type: "string",
                            enum: [
                                "positive",
                                "negative",
                                "neutral"
                            ]
                        },
                        title: {
                            description: "The title of the call, in 10 words or less, that captures the main topic of the call.",
                            type: "string"
                        },
                    },
                    required: [
                        "sentiment",
                        "title"
                    ]
                },
            },
            backgroundDenoisingEnabled: study.backgroundDenoisingEnabled || true,
            messagePlan: {
                idleMessages: study.messagePlan?.idleMessages || [
                    "Can you hear me?",
                    "Are you there?",
                ]
            }
        };

        return vapiAssistantOptions;
    }

    // Create a new session
    const createSession = async (session) => {
        try {
            let res = await fetch(`${process.env.REACT_APP_VOICEBRIDGE_API_CREATE_SESSION_URL}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${process.env.REACT_APP_VOICEBRIDGE_API_KEY}`
                },
                body: JSON.stringify({
                    session: session
                })
            });
            if (res.ok) {
                return true;
            } else {
                console.error(res);
                return false;
            }
        } catch (error) {
            console.error(error);
            return false;
        }
    }

    // Update session status
    const updateSessionStatus = async (sessionId, workspaceId, status) => {
        try {
            let res = await fetch(`${process.env.REACT_APP_VOICEBRIDGE_API_UPDATE_SESSION_STATUS_URL}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${process.env.REACT_APP_VOICEBRIDGE_API_KEY}`
                },
                body: JSON.stringify({
                    sessionId: sessionId,
                    workspaceId: workspaceId,
                    status: status,
                    userAttributes: {
                        name: name,
                        email: email
                    }
                })
            });
            if (res.ok) {
                return true;
            } else {
                console.error(res);
                return false;
            }
        } catch (error) {
            console.error(error);
            return false;
        }
    }

    // call start handler
    const startCallInline = async () => {

        setConnecting(true);

        // Call API to create a new session row in the database with status 'in progress'
        let id = uuidv4();
        let _session = {
            id: id,
            studyId: study.id,
            workspaceId: study.workspaceId,
            channel: 'page',
            isEmbedded: isEmbedded || false,
            inviteCode: inviteCode || null,
            status: 'in progress',
            visitorInfo: visitorInfo || null,
            createdAt: Date.now(),
            updatedAt: Date.now()
        }
        let res = await createSession(_session);
        if (res) {
            setSessionId(id);
            let vapiAssistantOptions = await generateAssistantOptions(id);
            if (vapiAssistantOptions) {
                try {
                    vapi.start(vapiAssistantOptions);
                } catch (error) {
                    console.error('Error starting study', error);
                    toast.error('Something went wrong. Please try again.');
                    setConnecting(false);
                    setConnected(false);
                }
            }
        } else {
            toast.error('Something went wrong. Please try again.');
            setConnecting(false);
            setConnected(false);
        }

    };

    const endCall = () => {
        vapi.stop();
    };

    if (isLoading || !study) {
        return (
            <div style={{ width: '100%' }}>
                <Row style={{ flexDirection: 'column', justifyContent: 'center', alignItems: 'center', marginLeft: 0, marginRight: 0, height: '100vh' }}>
                    <Spinner size="3" />
                    <Text size="3" as="div" style={{ marginTop: 20 }}>Loading survey, just a moment ...</Text>
                </Row>
            </div>
        );
    }

    return (
        <Row style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', marginLeft: 0, marginRight: 0, overflowY: 'auto' }}>
            <Col xs={12} sm={12} md={8} lg={11} xl={11} style={{ padding: 0, maxWidth: isPageWide ? 1200 : 400 }}>

                <Helmet>
                    <title>{study.sharing?.title || "Voicebridge"}</title>
                    <meta name="description" content={study.sharing?.greeting || "Voicebridge is a AI-moderated conversational surveys tool."} />
                    <meta property="og:title" content={study.sharing?.title || "Voicebridge"} />
                    <meta property="og:description" content={study.sharing?.greeting || "Voicebridge is a AI-moderated conversational surveys tool."} />
                    <meta property="og:image" content={study.stimulus || "/logo.svg"} />
                    <meta property="og:url" content={window.location.href} />
                    <meta property="og:type" content="website" />
                    <meta property="og:site_name" content="Voicebridge" />
                </Helmet>

                <Row style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', marginLeft: 0, marginRight: 0 }}>
                        
                        {study.stimulus && (
                            <Col xs={12} sm={12} md={12} lg={6} xl={6} style={{ padding: 0 }}>
                                <Image src={study.stimulus} alt="Study stimulus" style={{ width: '100%', height: isPageWide ? '60vh' : '24vh', borderRadius: 10, objectFit: isPageWide ? 'contain' : 'cover' }} />
                            </Col>
                        )}

                        <Col xs={12} sm={12} md={12} lg={6} xl={6} style={{ padding: 0, textAlign: 'center' }}>

                            {/* Study agent avatar */}
                            {study.sharing?.avatar && (
                                <div style={{ width: '100%', marginTop: 20 }}>
                                    <Image src={study.sharing?.avatar} alt="Study agent avatar" style={{ height: 50, width: 50, objectFit: 'cover', borderRadius: 5 }} />
                                </div>
                            )}

                            {/* Study name */}
                            {study.sharing?.title && (
                                <Row style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', marginLeft: 0, marginRight: 0, marginTop: 20 }}>
                                    <Col xs={12} sm={11} md={10} style={{ padding: 0 }}>
                                        <Text size='2' as='div' color="gray" weight="bold">{study.sharing?.title}</Text>
                                    </Col>
                                </Row>
                            )}

                            {/* Study greeting */}
                            {study.sharing?.greeting &&  (
                                <Row style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', marginLeft: 0, marginRight: 0 }}>
                                    <Col xs={12} sm={11} md={10} lg={8} xl={8} style={{ padding: 10 }}>
                                        <Text size={isPageWide && !connected ? '5' : '3'} as='div'>{study.sharing?.greeting}</Text>
                                    </Col>
                                </Row>
                            )}

                            {/* Assistant captions */}
                            {connected && (
                                <Row style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', marginLeft: 0, marginRight: 0, marginTop: 10, height: isPageWide ? 100 : 80 }}>
                                <Col xs={12} sm={11} md={10} lg={8} xl={8} style={{ padding: 0, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Text size={isPageWide ? '3' : '2'} as='div' style={{ fontFamily: 'monospace' }}>{assistantCaptions.slice(0, 100)}</Text>
                                    </Col>
                                </Row>
                            )}

                            {/* Red start/stop button */}
                            <Row style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', marginLeft: 0, marginRight: 0, marginTop: 20, marginBottom: 20 }}>

                                {conversationEnded ? (
                                    <div style={{ marginTop: 0 }}>
                                        <Text size='2' as='div' weight="bold">The session has ended</Text>
                                        <Button variant="soft" color="gray" size="3" style={{ marginTop: 10 }} onClick={() => {
                                            setConversationEnded(false);
                                            setConnected(false);
                                            setConnecting(false);
                                            window.location.reload();
                                        }}>
                                            <ArrowClockwise size={16} weight="bold" /> Restart
                                        </Button>
                                    </div>
                                ) : (
                                    <AudioRecorder
                                        isRecording={connected}
                                        volumeLevel={volumeLevel}
                                        onStart={startCallInline}
                                        onStop={endCall}
                                        isConnecting={connecting}
                                    />
                                )}
                            </Row>

                        </Col>
                    </Row>

                <Row style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', marginLeft: 0, marginRight: 0, marginTop: 20 }}>
                    <Col xs={12} sm={10} md={7} lg={5} xl={4} style={{ padding: 10, textAlign: 'center' }}>
                        <Text size='1' as='div' >Note: This is a voice-based experience and requires access to your microphone. You will be talking to an AI moderator.</Text>
                    </Col>
                </Row>

                <Row style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', marginLeft: 0, marginRight: 0, marginTop: 10 }}>
                    <Text size='1' as='div' color="gray" style={{ marginRight: 5 }}>Powered by</Text>
                    <Image src={theme === "dark-theme" ? "/logo-light.svg" : "/logo.svg"} style={{ width: 100, cursor: 'pointer' }} onClick={() => {
                        window.open('https://voicebridgeai.com', '_blank', 'noopener,noreferrer');
                    }} />
                </Row>

                <Toaster position='top-center' toastOptions={{ className: 'toast' }} />
            </Col>
        </Row>
    );

}